import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { Builder } from '@builder.io/react';
import { css } from '@emotion/react';
import { Colors } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { get } from 'lodash';
import { forwardRef } from 'react';
import { COLOR_KEYS } from '../../constants';
import { useAsyncData } from '../../content-renderers/Entry';
import { useRendererContext } from '../../content-renderers/RendererContext';
export const LIGHT_BACKGROUND_COLORS = [
    COLOR_KEYS.acidWash,
    COLOR_KEYS.bolt,
    COLOR_KEYS.cream,
    COLOR_KEYS.neutral200,
    COLOR_KEYS.lavender,
    COLOR_KEYS.neutral100,
    COLOR_KEYS.peach,
    COLOR_KEYS.sky,
    COLOR_KEYS.transparent,
    // Deprecated
    COLOR_KEYS.white,
    COLOR_KEYS.gray5,
    COLOR_KEYS.gray10,
    COLOR_KEYS.ocean5,
    COLOR_KEYS.ocean10,
    COLOR_KEYS.sun5,
    COLOR_KEYS.sun10,
    COLOR_KEYS.lava5,
    COLOR_KEYS.lava10,
];
function getLinkColor(backgroundColor) {
    if (backgroundColor &&
        !LIGHT_BACKGROUND_COLORS.some((color) => color === backgroundColor)) {
        return 'inherit';
    }
    return Colors.blue;
}
function getLinkHoverColor(backgroundColor) {
    if (backgroundColor &&
        !LIGHT_BACKGROUND_COLORS.some((color) => color === backgroundColor)) {
        return 'inherit';
    }
    return Colors.night;
}
const Styles = labelStyles('RichText', {
    default: (backgroundColor) => css({
        a: {
            color: getLinkColor(backgroundColor),
            textDecoration: 'underline',
            '&:hover': {
                color: getLinkHoverColor(backgroundColor),
                textDecoration: 'underline',
            },
        },
        ul: {
            listStyle: 'none',
            marginLeft: '32px',
            li: {
                position: 'relative',
                '&:before': {
                    content: '"•"',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'baseline',
                    position: 'absolute',
                    fontSize: '16px',
                    marginLeft: '-1em',
                    height: '100%',
                },
                '&:not(:last-child)': {
                    margin: '21px 0',
                },
            },
        },
        ol: {
            listStyle: 'none',
            marginLeft: '32px',
            li: {
                counterReset: 'list-1 list-2',
                counterIncrement: 'list-0',
                '&:not(:last-child)': {
                    margin: '21px 0',
                },
                '&:before': {
                    content: 'counter(list-0, decimal) ". "',
                    marginLeft: '-1em',
                },
                '&.ql-indent-1': {
                    counterIncrement: 'list-1',
                    counterReset: 'list-2',
                    '&:before': {
                        content: 'counter(list-1, lower-alpha) ". "',
                    },
                },
                '&.ql-indent-2': {
                    counterIncrement: 'list-2',
                    counterReset: 'none',
                    '&:before': {
                        content: 'counter(list-2, lower-roman) ". "',
                    },
                },
            },
        },
        'li.ql-indent-1': {
            paddingLeft: '2em',
        },
        'li.ql-indent-2': {
            paddingLeft: '4em',
        },
        'p:not(:first-of-type)': {
            marginTop: '10px',
        },
    }),
    reset: css({
        '& > *': {
            all: 'inherit',
        },
    }),
});
// regex to match all values between double curly braces with a limit of 100 characters (e.g. {{foo.bar}})
export const DATA_BINDING_REGEX = /(\\?)(\{\{(.{0,100}?)\}\})/g;
export const bindAsyncDataToHTML = ({ html, asyncData, }) => {
    return html.replaceAll(DATA_BINDING_REGEX, (_, escapeCharacter, expression, contents) => {
        var _a;
        if (!!escapeCharacter || !asyncData) {
            return expression;
        }
        const [path, fallbackValue] = contents.split('||').map((s) => s.trim());
        if (!(Builder.isEditing && path.endsWith('help'))) {
            return get(asyncData, path) || fallbackValue || '';
        }
        /**
         * If path is just 'help', show the entire asyncData object,
         * otherwise show the value at the path with .help removed from the end
         */
        const value = path === 'help'
            ? asyncData
            : get(asyncData, (_a = path.split('.')) === null || _a === void 0 ? void 0 : _a.slice(0, -1)) ||
                fallbackValue ||
                '';
        // HTML to show in the editor if 'help' is provided as the key
        return `<code>
      <div>Available keys:</div>
      ${Object.keys(value || {})
            .map((key) => `<li>${key}</li>`)
            .join('')}
      </code>`;
    });
};
export const RichText = forwardRef(({ html, backgroundColor, disableDefaultStyles }, ref) => {
    const { dataSets } = useRendererContext();
    const asyncData = useAsyncData(dataSets);
    const htmlString = typeof html === 'number' ? String(html) : html !== null && html !== void 0 ? html : '';
    return (_jsx("div", { css: disableDefaultStyles ? Styles.reset : Styles.default(backgroundColor), dangerouslySetInnerHTML: {
            __html: dataSets || Builder.isEditing || Builder.isPreviewing
                ? bindAsyncDataToHTML({
                    html: htmlString,
                    asyncData,
                })
                : html,
        }, ref: ref }));
});
//# sourceMappingURL=RichText.js.map